import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentTools, type ToolCall, type ToolCallSummary } from '@/features/types/tools';
import { ToolCallContainer } from '../../ToolCallContainer';
import { ToolCallDataList } from '../../ToolCallDataList';
import { useToolCallParser, ToolCallState } from '../../useToolCallParser';
import { ToolCallError } from '../../ToolCallError';
import { MeetingCard } from './common/MeetingCard';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { ChatMarkDownFormatter } from '../../../ChatMarkDownFormatter';
import { formatTranscriptContent } from './common/format-utils';

interface GetTranscriptTool {
  name: AgentTools.chorus_get_transcript;
  args: {
    conversationId: string;
  };
}

export type GetTranscriptToolCall = ToolCall<GetTranscriptTool['name'], GetTranscriptTool['args']>;

export type GetTranscriptToolCallSummary = ToolCallSummary<
  GetTranscriptTool['name'],
  GetTranscriptTool['args']
>;

interface GetTranscriptResult {
  account: {
    id: number;
    name: string;
    type: string;
  };
  date: string;
  id: string;
  owner: {
    email: string;
    name: string;
    person_id: number;
    user_id: number;
  };
  participants?: {
    company_name: string;
    email: string;
    is_my_team: boolean;
    name: string;
    person_id: number;
    picture: string;
    title: string;
    type: string;
    user_id: number;
    zi_person_id: number;
  }[];
  subject: string;
  transcriptText: string;
}

export function GetTranscriptToolCall({ toolCall }: { toolCall: GetTranscriptToolCallSummary }) {
  const { t: tCommon } = useTranslation();
  const { t } = useTranslation('prince');
  const userTimezone = useUserTimezone();

  const { parsedResult, title } = useToolCallParser<GetTranscriptResult>(
    toolCall,
    ({ toolCallState }) => {
      switch (toolCallState) {
        case ToolCallState.Running:
          return t('toolCalls.chorus_get_transcript.title.whileRunning');
        case ToolCallState.Error:
          return t('toolCalls.chorus_get_transcript.title.errors');
        case ToolCallState.Success:
          return t('toolCalls.chorus_get_transcript.title.success');
      }
    }
  );

  const getFormattedMeetingDate = useCallback(
    (date: string) =>
      tCommon('format.date', {
        date: new Date(date),
        formatParams: {
          date: {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            timezone: userTimezone,
          },
        },
      }),
    [tCommon, userTimezone]
  );

  const formattedTranscriptText = useMemo(() => {
    if (!parsedResult?.results?.transcriptText) return '';
    return formatTranscriptContent(parsedResult.results.transcriptText);
  }, [parsedResult]);

  const tabs = [
    {
      id: 'results',
      label: t('toolCalls.results'),
      showLoadingWhileRunning: true,
      content: parsedResult ? (
        !parsedResult.hasErrors ? (
          <MeetingCard
            subject={parsedResult.results.subject}
            formattedDate={getFormattedMeetingDate(parsedResult.results.date)}
            participants={(parsedResult.results.participants || []).map(participant => ({
              id: participant.person_id.toString(),
              name: participant.name,
            }))}
            accountName={parsedResult.results.account.name}
          >
            <ChatMarkDownFormatter content={formattedTranscriptText} />
          </MeetingCard>
        ) : (
          <ToolCallError parsedResult={parsedResult} />
        )
      ) : null,
    },
    {
      id: 'parameters',
      label: t('toolCalls.parameters'),
      content: (
        <ToolCallDataList
          items={[
            {
              label: t('toolCalls.chorus.conversationId'),
              value: toolCall.args.conversationId,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <ToolCallContainer
      toolName={toolCall.name}
      title={title}
      isRunning={toolCall.isRunning}
      tabs={tabs}
    />
  );
}
